import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { rangeFromPreset, type Granularity, type TimeRange, type TimeRangePreset } from "@/lib/timeRange";

const VALID_GRAN: Granularity[] = ["raw", "2m", "5m", "15m", "h", "d", "w", "m", "y"];
const VALID_PRESETS: TimeRangePreset[] = ["live-1h", "live-6h", "today", "24h", "7d", "30d", "ytd", "custom"];

/**
 * Persist (start, end, gran, preset) in the URL so a deep-link to a forensic
 * window restores precisely. Falls back to the supplied default preset on
 * first mount.
 *
 * Relative presets (live-1h … ytd) are treated as the source of truth:
 * restoring re-anchors them to now (a bookmarked "24h" means NOW-24h, not
 * the day it was bookmarked — PERS-13), and while one is active the window
 * auto-follows now on a 60 s interval (HIST-03). Only "custom" (or a
 * missing/invalid preset) restores absolute start/end verbatim.
 */
export function useUrlTimeRange(defaultPreset: TimeRangePreset = "24h"): [TimeRange, (r: TimeRange) => void] {
  const [params, setParams] = useSearchParams();
  const initial = useMemo<TimeRange>(() => {
    const start = Number(params.get("start"));
    const end = Number(params.get("end"));
    const gran = params.get("gran") as Granularity | null;
    const preset = params.get("preset") as TimeRangePreset | null;
    const validGran = gran && VALID_GRAN.includes(gran) ? gran : null;
    // Relative preset wins over stored start/end: re-anchor to now. A
    // valid gran in the URL is kept (gran clicks leave the preset active,
    // so a carried gran is a deliberate operator choice).
    if (preset && VALID_PRESETS.includes(preset) && preset !== "custom") {
      const r = rangeFromPreset(preset);
      return validGran ? { ...r, gran: validGran } : r;
    }
    if (start && end && validGran) {
      return { start, end, gran: validGran, preset: "custom" };
    }
    return rangeFromPreset(defaultPreset);
  }, [defaultPreset]);
  const [range, setRange] = useState<TimeRange>(initial);

  // Auto-follow: while a relative preset is active, re-anchor end=now every
  // 60 s so a chart left open during an incident keeps up. Skip the write
  // when the recomputed end is within 30 s of the current one to avoid
  // pointless query churn.
  useEffect(() => {
    if (range.preset === "custom") return;
    const id = window.setInterval(() => {
      setRange((prev) => {
        if (prev.preset === "custom") return prev;
        const next = rangeFromPreset(prev.preset);
        if (Math.abs(next.end - prev.end) <= 30_000) return prev;
        return { ...next, gran: prev.gran };
      });
    }, 60_000);
    return () => window.clearInterval(id);
  }, [range.preset]);

  // Sync to URL when range changes. Built from the LIVE URL
  // (window.location.search — synchronously current after each replace
  // navigation under BrowserRouter), NOT via the functional setParams
  // form: react-router 6.30 hands functional updaters the RENDER-TIME
  // params snapshot, so two URL writers firing in the same commit (this
  // effect + a sibling ?keys= sync) would clobber each other's params
  // (HIST-02). No-op writes are skipped so this effect never navigates
  // when the URL already matches (e.g. warm remounts from a deep link).
  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    if (
      next.get("start") === String(range.start) &&
      next.get("end") === String(range.end) &&
      next.get("gran") === range.gran &&
      next.get("preset") === range.preset
    ) return;
    next.set("start", String(range.start));
    next.set("end", String(range.end));
    next.set("gran", range.gran);
    next.set("preset", range.preset);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end, range.gran, range.preset]);

  const setter = useCallback((r: TimeRange) => setRange(r), []);
  return [range, setter];
}
